import { LivenessError } from "@aws-amplify/ui-react-liveness/dist/types/components/FaceLivenessDetector/service";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { ChangeEvent } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { applicantExamGetAction } from "@actions/ApplicantAction";

import { post } from "@api/httpClient";

import { Liveness } from "@shared/components/liveness";
import { LivenessResult } from "@shared/components/liveness/partials";
import Message from "@shared/services/message";

import { CameraPermissionStep } from "./cameraPermission/CameraPermission";
import { CautionStep } from "./cautionStep/CautionStep";
import { FaceVerificationStep } from "./faceVerificationStep/FaceVerificationStep";
import { FairnessCompleteStep } from "./fairnessComplete/FairnessComplete";
import { FairnessStep } from "./fairnessStep/FairnessStep";
import { FairnessFormStep } from "./formStep/FormStep";
import { ModalKeyEnum } from "./types";
import { REASON_SUBMISSION_MAX_LENGTH } from "./utils";

interface Step {
  titleId: string;
  buttonId?: string;
  component: JSX.Element;
  className?: string;
  onNext?: () => void;
  backButton?: {
    label: string;
    onClick: () => void;
  };
}

const postRefusalReason = async (data: {
  comment: string;
  applicantExamId: string;
}) => {
  const response = await post(
    `/api/applicants/exams/${data.applicantExamId}/webcam/refusal`,
    {
      data: { reason: data.comment },
    },
  );
  return response.data;
};

export const useModalSteps = (
  onStartExam: () => void,
  isWebcamRequired: boolean,
  applicantExamId: string,
  hasVerifiedLiveness: boolean,
  isTermAccepted: boolean,
  faceVerified: boolean,
  hasReason?: boolean,
) => {
  let initialNextButtonDisabled = true;
  let firstStep = ModalKeyEnum.CAUTION;
  if (isTermAccepted) {
    firstStep = ModalKeyEnum.FAIRNESS;
  }
  if (faceVerified) {
    firstStep = ModalKeyEnum.CAMERA_PERMISSION;
    initialNextButtonDisabled = false;
  } else if (hasReason) {
    firstStep = ModalKeyEnum.AFTER_FORM_START;
  }
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState<ModalKeyEnum>(firstStep);
  const [livenessError, setLivenessError] = useState<LivenessError | null>(
    null,
  );

  const mutation = useMutation({ mutationFn: postRefusalReason });

  const [showMaxStringValidationError, setShowMaxStringValidationError] =
    useState(false);

  const [nextButtonDisabled, setNextButtonDisabled] = useState(
    initialNextButtonDisabled,
  );

  const [fairnessComment, setFairnessComment] = useState("");

  const formSubmissionErrorMessage =
    Message.getMessageByKey("error.form.submit");

  const onFairCommentChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setFairnessComment(e.target.value);
    if (!e.target.value) {
      setNextButtonDisabled(true);
      setShowMaxStringValidationError(false);
      return;
    }
    if (e.target.value.length > REASON_SUBMISSION_MAX_LENGTH) {
      setNextButtonDisabled(true);
      setShowMaxStringValidationError(true);
      return;
    }
    setShowMaxStringValidationError(false);
    setNextButtonDisabled(false);
  };

  const modalSteps: Record<ModalKeyEnum, Step> = {
    [ModalKeyEnum.CAUTION]: {
      titleId: "editor-exam-detail.modals.firstModal.title",
      buttonId: "action.next",
      component: (
        <CautionStep
          onTermsChange={(e) => {
            setNextButtonDisabled(!e.target.checked);
          }}
        />
      ),
      onNext: () => {
        setActiveStep(
          hasVerifiedLiveness
            ? ModalKeyEnum.VERIFICATION_SUCCESS
            : ModalKeyEnum.FAIRNESS,
        );
      },
    },
    [ModalKeyEnum.FAIRNESS]: {
      titleId: "editor-exam-detail.modals.secondModal.title",
      buttonId: "action.next",
      component: (
        <FairnessStep
          isWebcamRequired={isWebcamRequired}
          onOptionalClick={() => {
            setActiveStep(ModalKeyEnum.OPTIONAL_FORM);
            setNextButtonDisabled(!fairnessComment);
          }}
        />
      ),
      backButton: {
        label: "action.prev",
        onClick: () => {
          setNextButtonDisabled(true);
          setActiveStep(ModalKeyEnum.CAUTION);
        },
      },
      onNext: () => setActiveStep(ModalKeyEnum.VERIFICATION),
    },
    [ModalKeyEnum.VERIFICATION]: {
      titleId: "editor-exam-detail.modals.thirdModal.title",
      buttonId: "button.verify",
      component: <FaceVerificationStep />,
      onNext: () => setActiveStep(ModalKeyEnum.CAMERA_PERMISSION),
      backButton: {
        label: "action.prev",
        onClick: () => setActiveStep(ModalKeyEnum.FAIRNESS),
      },
    },
    [ModalKeyEnum.CAMERA_PERMISSION]: {
      titleId: "editor-exam-detail.modals.fourthModal.title",
      buttonId: "",
      component: (
        <CameraPermissionStep
          faceVerified={faceVerified}
          onPermissionGranted={(permissionGranted) => {
            if (permissionGranted) setActiveStep(ModalKeyEnum.LIVENESS_START);
          }}
        />
      ),
    },
    [ModalKeyEnum.LIVENESS_START]: {
      titleId: "editor-exam-detail.modals.fourthModal.title",
      component: (
        <Liveness
          onSuccess={() => {
            // refetch the exam to get the updated face verification status
            dispatch(applicantExamGetAction.request());
            setActiveStep(ModalKeyEnum.VERIFICATION_SUCCESS);
          }}
          onFail={(err) => {
            setLivenessError(err ?? null);
            setActiveStep(ModalKeyEnum.VERIFICATION_FAILED);
          }}
        />
      ),
      className: "code-c-liveness-walkthrough",
    },
    [ModalKeyEnum.VERIFICATION_FAILED]: {
      titleId: "editor-exam-detail.modals.fourthModal.title",
      buttonId: "liveness.tryAgainText",
      component: <LivenessResult type="error" livenessError={livenessError} />,
      onNext: () => setActiveStep(ModalKeyEnum.LIVENESS_START),
      className: "code-liveness-result-modal",
    },
    [ModalKeyEnum.VERIFICATION_SUCCESS]: {
      titleId: "editor-exam-detail.modals.fourthModal.title",
      buttonId: "button.startExam",
      component: <LivenessResult type="success" />,
      className: "code-liveness-result-modal",
      onNext: () => {
        onStartExam();
      },
    },
    [ModalKeyEnum.OPTIONAL_FORM]: {
      titleId: "editor-exam-detail.modals.secondModal.title",
      buttonId: "action.submit",
      component: (
        <FairnessFormStep
          fairnessComment={fairnessComment}
          onFairCommentChange={onFairCommentChange}
          validationError={showMaxStringValidationError}
          loading={mutation.isLoading}
        />
      ),
      onNext: async () => {
        mutation.mutate(
          { comment: fairnessComment, applicantExamId },
          {
            onSuccess: () => {
              // refetch the exam to get the updated status
              dispatch(applicantExamGetAction.request());
              setActiveStep(ModalKeyEnum.AFTER_FORM_START);
            },
            onError: (error) => {
              toast.error(formSubmissionErrorMessage);
            },
          },
        );
      },
      backButton: {
        label: "action.back",
        onClick: () => {
          setNextButtonDisabled(false);
          setActiveStep(ModalKeyEnum.FAIRNESS);
        },
      },
    },
    [ModalKeyEnum.AFTER_FORM_START]: {
      titleId: "editor-exam-detail.modals.secondModal.title",
      buttonId: "button.startExam",
      component: <FairnessCompleteStep />,
      onNext: () => {
        onStartExam();
      },
    },
  };

  const activeModal = modalSteps[activeStep];

  const goToNextStep = () => {
    if (activeModal.onNext) {
      activeModal.onNext();
    }
  };

  const resetSteps = () => {
    if (faceVerified) {
      setActiveStep(ModalKeyEnum.CAMERA_PERMISSION);
      setNextButtonDisabled(false);
    } else {
      setActiveStep(ModalKeyEnum.CAUTION);
      setNextButtonDisabled(true);
    }
  };

  return {
    activeModal,
    goToNextStep,
    resetSteps,
    nextButtonDisabled,
  };
};
